import { useParams, useNavigate } from "react-router-dom";
import { Alert, Button, Chip, Rating, Skeleton, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from "@mui/material";
import { useEffect, useState } from 'react';
import { ApiRequest } from "GlobalFunctions";
import { Helmet } from 'react-helmet';
import { FaWhatsapp } from "react-icons/fa";
import { MdVerified, MdHealing, MdDesktopWindows } from "react-icons/md";
import Ratings from "./Utils/Ratings";
import CredentialsGallery from "./Utils/CredentialsGallery";
import StarRateRoundedIcon from '@mui/icons-material/StarRateRounded';
import { CiStar, CiStethoscope } from "react-icons/ci";

export default function KinesiologistDetail(props: any) {
    const params = useParams<{ user_id: string }>();
    const user_id = props.id ?? params.user_id;
    const navigate = useNavigate();

    const [data, setData] = useState<any>({});
    const [loading, setLoading] = useState(true);

    const [openWhatsappForm, setOpenWhatsappForm] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        comuna: '',
        request: 'Necesito ayuda profesional'
    });

    const [openLoginAlert, setOpenLoginAlert] = useState(false);
    const [openRating, setOpenRating] = useState(false);
    const [ratingValue, setRatingValue] = useState<number | null>(0);
    const [ratingComment, setRatingComment] = useState('');
    const [ratingLoading, setRatingLoading] = useState(false);
    const [ratingDone, setRatingDone] = useState(false);
    const [ratingMessage, setRatingMessage] = useState('');
    const [ratingError, setRatingError] = useState('');
    const [ratingTouched, setRatingTouched] = useState(false);

    const handleOpenRating = () => {
        if (!localStorage.getItem('ltkn')) { setOpenLoginAlert(true); return; }
        setOpenRating(true);
    };

    const handleCloseRating = () => {
        setOpenRating(false);
        setRatingDone(false);
        setRatingMessage('');
        setRatingError('');
        setRatingValue(0);
        setRatingComment('');
        setRatingTouched(false);
    };

    const handleSubmitRating = async () => {
        if (!ratingValue) { setRatingTouched(true); return; }
        setRatingLoading(true);
        setRatingError('');
        await ApiRequest({
            url: '/ratings/submit',
            method: 'POST',
            body: { rated_user_id: data.user_id, rating_value: ratingValue, comment: ratingComment },
            setResponse: (response: any) => {
                setRatingLoading(false);
                if (response?.status === 'success') {
                    setRatingDone(true);
                    setRatingMessage(response.message || 'Reseña enviada. ¡Gracias!');
                } else {
                    setRatingError(response?.message || 'No se pudo enviar la reseña');
                }
            },
            onError: (msg: string) => {
                setRatingLoading(false);
                setRatingError(msg || 'No se pudo enviar la reseña');
            },
        });
    };



    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleWhatsappSubmit = async () => {
        const kineName = `${data.name ?? ''} ${data.lastname ?? ''}`.trim();
        const message = `Hola, soy ${formData.name}.\nTeléfono: ${formData.phone}\nComuna: ${formData.comuna}\nSolicitud: ${formData.request}\nDirigido a: Kinesiólogo ${kineName}`;
        const whatsappURL = `https://wa.me/+56${data.whatsapp}?text=${encodeURIComponent(message)}`;

        await ApiRequest({
            url: '/whatsapp/log',
            method: 'POST',
            body: {
                nurse_user_id: data.user_id,
                patient_name: formData.name,
                patient_phone: formData.phone,
                patient_comuna: formData.comuna,
                message,
            },
            setResponse: () => {},
            onError: () => {},
        });

        window.open(whatsappURL, '_blank');
        setOpenWhatsappForm(false);
    };

    const loadDetails = async () => {
        await ApiRequest({
            url: '/kinesiologists/details',
            method: 'GET',
            query: { user_id },
            setResponse: (response: any) => {
                setData(response);
                setLoading(false);
            },
            onError: () => {
                setLoading(false);
            },
        });
    };

    useEffect(() => {
        loadDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const kineJsonLd = data.name ? {
        "@context": "https://schema.org",
        "@type": "Physician",
        "name": `${data.name} ${data.lastname}`,
        "description": data.about_me || `Kinesiólogo${Array.isArray(data.locations_labels) && data.locations_labels.length ? ` en ${data.locations_labels.join(', ')}` : ''}.`,
        "url": `https://buscakine.cl/kinesiologos/${user_id}`,
        "image": data.image || undefined,
        "jobTitle": data.university_degree || "Kinesiólogo",
        "medicalSpecialty": "Physiotherapy",
        "areaServed": Array.isArray(data.locations_labels) ? data.locations_labels.map((l: string) => ({ "@type": "City", "name": l })) : [],
        ...(Number(data.average_rating) > 0 && {
            "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": String(Number(data.average_rating).toFixed(1)),
                "bestRating": "5",
                "worstRating": "1",
                "ratingCount": String(Number(data.rating_count) || 1)
            }
        })
    } : null;

    const kineTitle = data.name ? `${data.name} ${data.lastname} — Kinesiólogo` : 'Kinesiólogo';
    const kineDesc = data.about_me
        ? data.about_me.slice(0, 160)
        : `Kinesiólogo${Array.isArray(data.locations_labels) && data.locations_labels.length ? ` en ${data.locations_labels.join(', ')}` : ''} — buscakine.cl`;

    const skillsMap: Record<string, string> = data.skills_map || {};
    const specialtyPrices: Record<string, string> = data.specialty_prices || {};

    if (loading) return (
        <div id='nurses_details_container' className="content" style={{ minHeight: '90vh' }}>
            <div>
                {/* Header: imagen + datos básicos */}
                <div className='section_flex' style={{ gap: '24px', alignItems: 'flex-start' }}>
                    <Skeleton variant="rounded" width={180} height={180} style={{ flexShrink: 0, borderRadius: '12px' }} />
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <Skeleton variant="text" width="60%" height={38} />
                        <Skeleton variant="text" width="40%" height={22} />
                        <Skeleton variant="text" width="30%" height={22} />
                        <Skeleton variant="rounded" width={130} height={28} style={{ borderRadius: '16px' }} />
                        <Skeleton variant="text" width="45%" height={22} />
                        <Skeleton variant="text" width="35%" height={22} />
                    </div>
                </div>

                {/* Acerca de mí */}
                <div style={{ marginTop: '32px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <Skeleton variant="text" width="25%" height={30} />
                    <Skeleton variant="text" width="100%" height={20} />
                    <Skeleton variant="text" width="100%" height={20} />
                    <Skeleton variant="text" width="85%" height={20} />
                    <Skeleton variant="text" width="90%" height={20} />
                </div>

                {/* Atenciones */}
                <div style={{ marginTop: '28px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <Skeleton variant="text" width="28%" height={30} />
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <Skeleton variant="rounded" width={110} height={32} style={{ borderRadius: '16px' }} />
                        <Skeleton variant="rounded" width={130} height={32} style={{ borderRadius: '16px' }} />
                        <Skeleton variant="rounded" width={95}  height={32} style={{ borderRadius: '16px' }} />
                        <Skeleton variant="rounded" width={120} height={32} style={{ borderRadius: '16px' }} />
                    </div>
                </div>

                {/* Comunas */}
                <div style={{ marginTop: '28px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <Skeleton variant="text" width="20%" height={30} />
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <Skeleton variant="rounded" width={100} height={32} style={{ borderRadius: '16px' }} />
                        <Skeleton variant="rounded" width={120} height={32} style={{ borderRadius: '16px' }} />
                        <Skeleton variant="rounded" width={90}  height={32} style={{ borderRadius: '16px' }} />
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <Helmet>
                <title>{kineTitle}</title>
                <meta name="description" content={kineDesc} />
                <meta property="og:title" content={kineTitle} />
                <meta property="og:description" content={kineDesc} />
                <meta property="og:type" content="profile" />
                <meta property="og:url" content={`https://buscakine.cl/kinesiologos/${user_id}`} />
                {data.image && <meta property="og:image" content={data.image} />}
                {kineJsonLd && <script type="application/ld+json">{JSON.stringify(kineJsonLd)}</script>}
            </Helmet>
            <div id='nurses_details_container' className="content" style={{ animation: 'fadeInProfile 0.25s ease', minHeight: '90vh' }}>
                <div className='nurses_buttons'>
                    {data.whatsapp && (
                        <Button
                            variant="contained"
                            color="success"
                            onClick={() => setOpenWhatsappForm(true)}
                        >
                            <FaWhatsapp size={23} style={{ marginRight: '7px' }} />
                            WhatsApp
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        onClick={handleOpenRating}
                    >
                        Evaluar Kinesiólogo
                    </Button>
                </div>

                <div>
                    <div className='section_flex'>
                        <div style={{ width: '180px', height: '180px', flexShrink: 0, borderRadius: '12px', overflow: 'hidden', background: '#f0f4f8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {data.image
                                ? <img src={data.image} alt="Imagen de perfil" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                : <Skeleton variant="rounded" width={180} height={180} />
                            }
                        </div>

                        <div className='account_right'>
                            <div className='info_column'>
                                <h1 className='user_name'>{data.name + " " + data.lastname}</h1>
                                <div className="items_row" style={{ gap: '6px', marginBottom: '4px', flexWrap: 'wrap' }}>
                                    {data.badge && (
                                        <Chip
                                            label={data.badge}
                                            size="small"
                                            icon={<CiStar size={20} />}
                                            sx={{
                                                background: 'linear-gradient(45deg, #FFD700 30%, #DAA520 90%)', color: '#6B4423',
                                                maxWidth: '100%', height: 'auto',
                                                '& .MuiChip-label': { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'clip', padding: '4px 8px' },
                                            }}
                                        />
                                    )}
                                </div>
                                <span className='user_subtitle'>{data.university_degree}</span>
                                <span className='user_subtitle'>{data.region}</span>

                                <Rating
                                    value={data.average_rating || 0}
                                    readOnly
                                    precision={0.5}
                                    icon={<StarRateRoundedIcon fontSize="inherit" />}
                                    emptyIcon={<StarRateRoundedIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
                                />

                                <h3 className='service_title'>Servicio Ofrecido</h3>
                                <div className='service_row'>
                                    <span className='service_label'>{data.service}</span>
                                    <span className='service_price'>{data.price}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='account_flex'>
                        <div className='account_left'>
                            <div>
                                <h2 className='titles'><CiStethoscope size={25} /> Acerca de mi</h2>
                                <p>{data.about_me}</p>
                            </div>

                            <div>
                                <h2 className='titles'>Prestaciones</h2>
                                <div className='services_row'>
                                    {data.advanced_wound_care && (
                                        <span className='item_icon'><MdHealing /> Kinesiología a Domicilio</span>
                                    )}
                                    {data.online_advice && (
                                        <span className='item_icon'><MdDesktopWindows /> Asesoría Online</span>
                                    )}
                                </div>
                            </div>

                            {/* Atenciones y precios */}
                            {Object.keys(skillsMap).length > 0 && (
                                <div>
                                    <h2 className='titles'>Atenciones</h2>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                                        {Object.entries(skillsMap).map(([skillId, label]) => {
                                            const price = specialtyPrices?.[skillId];
                                            return (
                                                <div key={skillId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                                                    <span style={{ fontSize: 14, color: '#334155' }}>{label}</span>
                                                    {price ? (
                                                        <span style={{ fontSize: 14, fontWeight: 700, color: '#005F6A' }}>
                                                            ${parseInt(price).toLocaleString('es-CL')}
                                                        </span>
                                                    ) : (
                                                        <span style={{ fontSize: 13, color: '#94a3b8' }}>A convenir</span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Registro Superintendencia */}
                            {data.superintendencia_registro && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '4px' }}>
                                    <span className='item_icon'>
                                        <MdVerified size={16} /> Reg. Superintendencia de Salud: {data.superintendencia_registro}
                                    </span>
                                </div>
                            )}

                            <div>
                                <h2 className='titles'>Comunas</h2>
                                <div className='items_row'>
                                    {data.locations_labels?.map((location: any, index: number) => (
                                        <Chip label={location} key={index} color="primary" sx={{ color: '#fff' }} />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <CredentialsGallery nurse_id={data.nurse_id} />

                <Ratings user_id={data.user_id} />
            </div>

            <Dialog open={openWhatsappForm} onClose={() => setOpenWhatsappForm(false)} fullWidth maxWidth="sm">
                <DialogTitle>Contacto por WhatsApp</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Nombre"
                        name="name"
                        margin="normal"
                        value={formData.name}
                        onChange={handleFormChange}
                    />
                    <TextField
                        fullWidth
                        label="Teléfono"
                        name="phone"
                        margin="normal"
                        value={formData.phone}
                        onChange={handleFormChange}
                    />
                    <TextField
                        fullWidth
                        label="Comuna"
                        name="comuna"
                        margin="normal"
                        value={formData.comuna}
                        onChange={handleFormChange}
                    />
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Solicitud"
                        name="request"
                        margin="normal"
                        value={formData.request}
                        onChange={handleFormChange}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenWhatsappForm(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleWhatsappSubmit}>Enviar</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openRating} onClose={handleCloseRating} fullWidth maxWidth="sm">
                <DialogTitle>Evaluar Kinesiólogo</DialogTitle>
                <DialogContent>
                    {ratingDone ? (
                        <Alert severity="success" sx={{ mt: 1 }}>
                            {ratingMessage}
                        </Alert>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '8px' }}>
                            {ratingError && <Alert severity="error">{ratingError}</Alert>}
                            <div>
                                <p style={{ margin: '0 0 4px 0', fontWeight: 500 }}>Calificación *</p>
                                <Rating
                                    value={ratingValue}
                                    onChange={(_, v) => setRatingValue(v)}
                                    precision={1}
                                    size="large"
                                    icon={<StarRateRoundedIcon fontSize="inherit" />}
                                    emptyIcon={<StarRateRoundedIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
                                />
                                {ratingTouched && !ratingValue && (
                                    <p style={{ color: '#d32f2f', fontSize: '0.75rem', margin: '4px 0 0 0' }}>
                                        Debes seleccionar una calificación
                                    </p>
                                )}
                            </div>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Comentario (opcional)"
                                value={ratingComment}
                                onChange={(e) => setRatingComment(e.target.value)}
                            />
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    {ratingDone ? (
                        <Button variant="contained" onClick={handleCloseRating}>Cerrar</Button>
                    ) : (
                        <>
                            <Button onClick={handleCloseRating}>Cancelar</Button>
                            <Button
                                variant="contained"
                                onClick={handleSubmitRating}
                                disabled={ratingLoading}
                            >
                                {ratingLoading ? 'Guardando...' : 'Enviar Reseña'}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog open={openLoginAlert} onClose={() => setOpenLoginAlert(false)}>
                <DialogTitle>Atención</DialogTitle>
                <DialogContent>
                    Es necesario iniciar sesión para dejar una reseña.
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenLoginAlert(false)}>Cerrar</Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setOpenLoginAlert(false);
                            navigate("/login");
                        }}
                    >
                        Ir a Login
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
